import type {
  AnnotatedFrame,
  AppConfig,
  FramePayload,
  Manifest,
  PendingItem,
  Point,
  TermDetail,
  TermSummary,
  VoteResult,
} from '../shared/types';

const ADMIN_KEY = 'daiconnotation.admin';

export function getAdminAuth(): string | null {
  return sessionStorage.getItem(ADMIN_KEY);
}
export function setAdminAuth(user: string, pass: string): void {
  sessionStorage.setItem(ADMIN_KEY, btoa(`${user}:${pass}`));
}
export function clearAdminAuth(): void {
  sessionStorage.removeItem(ADMIN_KEY);
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}, admin = false): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined) headers.set('content-type', 'application/json');
  if (admin) {
    const auth = getAdminAuth();
    if (!auth) throw new ApiError('not signed in', 401);
    headers.set('authorization', `Basic ${auth}`);
  }
  const res = await fetch(path, { ...init, headers });
  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const msg =
      data && typeof data === 'object' && 'error' in data && typeof data.error === 'string'
        ? data.error
        : `HTTP ${res.status}`;
    throw new ApiError(msg, res.status);
  }
  return data as T;
}

export const api = {
  config: () => request<AppConfig>('/api/config'),
  manifest: () => request<Manifest>('/manifest.json'),
  searchTerms: (q: string) => request<TermSummary[]>(`/api/terms?q=${encodeURIComponent(q)}`),
  termDetail: (id: number) => request<TermDetail>(`/api/terms/${id}`),
  annotated: () => request<AnnotatedFrame[]>('/api/frames/annotated'),
  framePolygons: (ms: number, admin: boolean) =>
    request<FramePayload>(admin ? `/api/admin/frames/${ms}/polygons` : `/api/frames/${ms}/polygons`, {}, admin),
  vote: (connectionId: number, value: 1 | -1, token: string) =>
    request<VoteResult>(`/api/connections/${connectionId}/vote`, {
      method: 'POST',
      body: JSON.stringify({ value, token }),
    }),
  suggest: (polygonId: number, body: { term_id?: number; text?: string; synonyms?: string[]; token: string }) =>
    request<{ pending: boolean }>(`/api/polygons/${polygonId}/suggest`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  admin: {
    ping: () => request<void>('/api/admin/ping', {}, true),
    searchTerms: (q: string) => request<TermSummary[]>(`/api/admin/terms?q=${encodeURIComponent(q)}`, {}, true),
    createTerm: (text: string, synonyms: string[]) =>
      request<{ id: number }>('/api/admin/terms', { method: 'POST', body: JSON.stringify({ text, synonyms }) }, true),
    renameTerm: (id: number, text: string) =>
      request<{ ok: true }>(`/api/admin/terms/${id}`, { method: 'PUT', body: JSON.stringify({ text }) }, true),
    deleteTerm: (id: number) => request<{ ok: true }>(`/api/admin/terms/${id}`, { method: 'DELETE' }, true),
    addSynonym: (termId: number, text: string) =>
      request<{ id: number }>(`/api/admin/terms/${termId}/synonyms`, { method: 'POST', body: JSON.stringify({ text }) }, true),
    deleteSynonym: (id: number) => request<{ ok: true }>(`/api/admin/synonyms/${id}`, { method: 'DELETE' }, true),
    deleteSynonymByText: (termId: number, text: string) =>
      request<{ ok: true }>(
        `/api/admin/terms/${termId}/synonyms`,
        { method: 'DELETE', body: JSON.stringify({ text }) },
        true,
      ),

    createPolygon: (frameMs: number, points: Point[], note: string | null) =>
      request<{ id: number }>(
        `/api/admin/frames/${frameMs}/polygons`,
        { method: 'POST', body: JSON.stringify({ points, note }) },
        true,
      ),
    updatePolygon: (id: number, body: { points?: Point[]; note?: string | null }) =>
      request<{ ok: true }>(`/api/admin/polygons/${id}`, { method: 'PUT', body: JSON.stringify(body) }, true),
    deletePolygon: (id: number) => request<{ ok: true }>(`/api/admin/polygons/${id}`, { method: 'DELETE' }, true),

    attachTerm: (polygonId: number, termId: number) =>
      request<{ ok: true }>(
        `/api/admin/polygons/${polygonId}/terms`,
        { method: 'POST', body: JSON.stringify({ term_id: termId }) },
        true,
      ),
    deleteConnection: (id: number) => request<{ ok: true }>(`/api/admin/connections/${id}`, { method: 'DELETE' }, true),

    pending: () => request<PendingItem[]>('/api/admin/pending', {}, true),
    approve: (connectionId: number) =>
      request<{ ok: true }>(`/api/admin/connections/${connectionId}/approve`, { method: 'POST' }, true),
    reject: (connectionId: number) =>
      request<{ ok: true }>(`/api/admin/connections/${connectionId}/reject`, { method: 'POST' }, true),
  },
};
